import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle, ArrowRight, Calculator, ChevronDown, ChevronRight, Globe2, Headphones, Home,
  Loader2, MapPin, MessageCircle, Package, Phone, Plane, Search, ShieldAlert, Sparkles, Truck,
} from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { usePageMeta } from "../components/service/ServiceBits";
import api from "../lib/api";

const CHIPS = [
  ["Domestic", Truck], ["International", Globe2], ["Pricing & Payments", Calculator], ["Weight & Volume", Package],
  ["Pickup", Truck], ["Delivery", Package], ["Tracking", Search], ["Customs", Plane], ["Packing", Package],
  ["NRI Shipping", Globe2], ["eCommerce", Sparkles], ["Business", Truck], ["Prohibited Items", ShieldAlert],
  ["Claims & Refunds", AlertTriangle], ["General", Headphones],
];

const INFOGRAPHICS = {
  "Weight & Volume": {
    title: "How Chargeable Weight Works", testid: "faq-infographic-weight",
    steps: ["Actual Weight", "Volumetric Weight", "Higher of the Two", "Rounded per Billing Rule"],
  },
  "Tracking": {
    title: "Your Shipment's Journey", testid: "faq-infographic-tracking",
    steps: ["Booked", "Picked Up", "Processing", "In Transit", "Destination", "Delivered"],
  },
  "Customs": {
    title: "International Clearance Flow", testid: "faq-infographic-customs",
    steps: ["Pickup", "Export Processing", "Air Transit", "Customs", "Destination", "Delivery"],
  },
  "Claims & Refunds": {
    title: "How a Claim Moves", testid: "faq-infographic-claims",
    steps: ["Delivery", "Inspect", "Report", "Submit Documents", "Review"],
  },
};

function Infographic({ title, steps, testid }) {
  return (
    <div className="my-8 rounded-2xl bg-navy p-6" data-testid={testid}>
      <p className="text-[10px] font-bold uppercase tracking-wider text-saffron">{title}</p>
      <div className="mt-4 flex items-center gap-1 overflow-x-auto pb-1">
        {steps.map((s, i) => (
          <div key={s} className="flex shrink-0 items-center">
            <span className={`rounded-xl px-3.5 py-2.5 text-[11px] font-bold ${i === steps.length - 1 ? "bg-emerald-600 text-white" : "border border-white/15 bg-white/5 text-slate-200"}`}>{s}</span>
            {i < steps.length - 1 && <ChevronRight className="mx-0.5 h-4 w-4 shrink-0 text-saffron" />}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function FaqCenter() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [catNames, setCatNames] = useState([]);
  const [contact, setContact] = useState(null);
  const [cat, setCat] = useState("");
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(null);
  const debounce = useRef(null);

  usePageMeta("Courier FAQ | Domestic & International Courier Questions | IndiaFast",
    "Find answers about domestic and international courier services, shipping prices, volumetric weight, packing, customs, NRI shipments, tracking, prohibited items and more.", "/p/faq");

  useEffect(() => {
    api.get("/faqs/public").then((r) => {
      setItems(r.data.items);
      setCounts(r.data.counts);
      setCatNames(r.data.categories || Object.keys(r.data.counts || {}));
    }).catch(() => {});
    api.get("/public/home").then((r) => setContact(r.data.contact)).catch(() => {});
  }, []);

  // FAQPage JSON-LD — only visible published Q&As
  useEffect(() => {
    if (!items.length) return;
    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.id = "faq-jsonld";
    ld.text = JSON.stringify({
      "@context": "https://schema.org", "@type": "FAQPage",
      mainEntity: items.map((f) => ({ "@type": "Question", name: f.question, acceptedAnswer: { "@type": "Answer", text: f.answer } })),
    });
    document.head.appendChild(ld);
    return () => document.getElementById("faq-jsonld")?.remove();
  }, [items]);

  // debounced live search
  useEffect(() => {
    clearTimeout(debounce.current);
    if (!q.trim()) { setResults(null); return; }
    debounce.current = setTimeout(async () => {
      setSearching(true);
      try { const r = await api.post("/faqs/public/search", { q: q.trim() }); setResults(r.data.items); }
      catch { setResults([]); }
      finally { setSearching(false); }
    }, 350);
    return () => clearTimeout(debounce.current);
  }, [q]);

  const openFaq = (f) => {
    const key = f.id;
    setOpen(open === key ? null : key);
    if (open !== key) api.post(`/faqs/public/view/${f.id}`).catch(() => {});
  };

  const grouped = useMemo(() => {
    const map = {};
    const list = cat ? items.filter((f) => f.category === cat) : items;
    for (const f of list) (map[f.category] = map[f.category] || []).push(f);
    const order = catNames.length ? catNames : Object.keys(counts);
    return Object.fromEntries(Object.entries(map).sort(([a], [b]) => {
      const ai = order.includes(a) ? order.indexOf(a) : 999;
      const bi = order.includes(b) ? order.indexOf(b) : 999;
      return ai - bi || a.localeCompare(b);
    }));
  }, [items, cat, catNames, counts]);

  const mostAsked = useMemo(() => {
    const featured = items.filter((f) => f.featured);
    if (featured.length) return featured.slice(0, 6);
    return [...items].sort((a, b) => (b.views || 0) - (a.views || 0)).filter((f) => f.views > 0).slice(0, 6);
  }, [items]);

  const waNum = (contact?.phone || "").replace(/\D/g, "").slice(-10);
  const isSearching = results !== null;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="faqs-page">
      <Navbar />

      {/* HERO + SEARCH */}
      <section className="relative overflow-hidden bg-navy pb-20 pt-32 sm:pt-36" data-testid="faqs-hero">
        <div className="pointer-events-none absolute -right-24 top-0 h-80 w-80 rounded-full bg-saffron/15 blur-3xl" />
        <div className="tricolor-beam absolute inset-x-0 top-0 h-1.5" />
        <svg className="pointer-events-none absolute inset-0 h-full w-full opacity-20" aria-hidden="true">
          <line x1="10%" y1="70%" x2="40%" y2="40%" stroke="#f97316" strokeWidth="1" strokeDasharray="4 6" className="network-dash" />
          <line x1="40%" y1="40%" x2="75%" y2="60%" stroke="#16a34a" strokeWidth="1" strokeDasharray="4 6" className="network-dash" />
          <circle cx="10%" cy="70%" r="4" fill="#f97316" className="network-pulse" />
          <circle cx="75%" cy="60%" r="4" fill="#16a34a" className="network-pulse" style={{ animationDelay: "1s" }} />
        </svg>
        <div className="relative mx-auto max-w-4xl px-4 text-center sm:px-6">
          <p className="animate-fade-up text-xs font-bold uppercase tracking-[0.25em] text-saffron">Shipping Help Center</p>
          <h1 className="animate-fade-up stagger-1 mt-3 font-heading text-4xl font-extrabold tracking-tight text-white sm:text-5xl" data-testid="faqs-title">
            How Can We Help You <span className="text-saffron">Ship?</span>
          </h1>
          <p className="animate-fade-up stagger-2 mx-auto mt-4 max-w-xl text-sm text-slate-300 sm:text-base">
            Find quick answers about domestic courier, international shipping, packing, pricing, customs, tracking and more.
          </p>
          <div className="animate-fade-up stagger-3 relative mx-auto mt-8 max-w-2xl">
            <Search className={`absolute left-5 top-1/2 h-5 w-5 -translate-y-1/2 ${searching ? "animate-pulse text-saffron" : "text-slate-400"}`} />
            <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="faqs-search"
                   placeholder='Try "How is volumetric weight calculated?"'
                   className="w-full rounded-full border border-white/15 bg-white/10 py-4 pl-12 pr-5 text-sm text-white shadow-2xl outline-none backdrop-blur-xl placeholder:text-slate-400 focus:border-saffron" />
          </div>
          {/* category chips */}
          <div className="mt-7 flex gap-2 overflow-x-auto pb-1 sm:flex-wrap sm:justify-center sm:overflow-visible" data-testid="faqs-chips">
            {catNames.filter((c) => counts[c]).map((c) => {
              const Icon = (CHIPS.find(([name]) => name === c) || [null, Package])[1];
              return (
                <button key={c} onClick={() => { setCat(cat === c ? "" : c); setQ(""); setResults(null); }}
                        data-testid={`faqs-chip-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        className={`flex shrink-0 items-center gap-1.5 rounded-full px-3.5 py-2 text-[11px] font-bold transition-all ${cat === c ? "bg-saffron text-white shadow-lg shadow-saffron/30" : "border border-white/15 bg-white/5 text-slate-300 hover:bg-white/15"}`}>
                  <Icon className="h-3.5 w-3.5" /> {c} <span className="opacity-60">({counts[c]})</span>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* QUICK HELP CARDS */}
      <section className="mx-auto max-w-7xl px-4 py-12 sm:px-6" data-testid="faqs-quick-cards">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[
            [Truck, "Domestic Courier", "Questions about shipping within India", () => { setCat("Domestic"); }, "View Domestic FAQs", null],
            [Globe2, "International Courier", "Shipping from India to other countries", () => { setCat("International"); }, "View International FAQs", null],
            [Calculator, "Weight & Pricing", "Understand chargeable weight & calculations", null, "Calculate Weight", "/pickup-request"],
            [ShieldAlert, "Prohibited Items", "Check items that may be restricted", null, "View Prohibited Items", "/p/prohibited"],
          ].map(([Icon, t, d, fn, btn, href], i) => {
            const Tag = href ? Link : "button";
            return (
              <div key={i} className="group rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition-all hover:-translate-y-1 hover:border-saffron/40 hover:shadow-xl" data-testid={`faqs-quick-${i}`}>
                <span className="grid h-12 w-12 place-items-center rounded-xl bg-saffron/10 text-saffron transition-colors group-hover:bg-saffron group-hover:text-white"><Icon className="h-6 w-6" /></span>
                <h3 className="mt-4 font-heading text-base font-bold text-navy">{t}</h3>
                <p className="mt-1 text-xs leading-relaxed text-slate-500">{d}</p>
                <Tag {...(href ? { to: href } : { type: "button", onClick: () => { setQ(""); setResults(null); fn(); document.getElementById("faqs-list")?.scrollIntoView({ behavior: "smooth" }); } })}
                     data-testid={`faqs-quick-cta-${i}`}
                     className="mt-4 inline-flex items-center gap-1.5 text-xs font-bold text-saffron hover:text-saffron-hover">
                  {btn} <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
                </Tag>
              </div>
            );
          })}
        </div>
      </section>

      <main id="faqs-list" className="mx-auto max-w-4xl scroll-mt-24 px-4 pb-16 sm:px-6">
        {isSearching ? (
          /* SEARCH RESULTS */
          <section data-testid="faqs-results">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-saffron">Search Results</p>
            <h2 className="mt-2 font-heading text-xl font-extrabold text-navy">
              {searching ? "Searching…" : `${results.length} answer${results.length !== 1 ? "s" : ""} for "${q}"`}
            </h2>
            {!searching && !results.length && (
              <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-10 text-center" data-testid="faqs-no-results">
                <Search className="mx-auto h-8 w-8 text-slate-300" />
                <p className="mt-3 font-heading text-base font-bold text-navy">No matching answer yet</p>
                <p className="mt-1 text-xs text-slate-500">Try different words — or ask our team directly below.</p>
              </div>
            )}
            <div className="mt-6 space-y-3">
              {results.map((f) => <FaqCard key={f.id} f={f} open={open === f.id} onOpen={() => openFaq(f)} prefix="faq-result" />)}
            </div>
          </section>
        ) : (
          <>
            {/* MOST ASKED */}
            {!!mostAsked.length && !cat && (
              <section className="mb-12" data-testid="faqs-most-asked">
                <p className="text-xs font-bold uppercase tracking-[0.25em] text-saffron">Most Asked</p>
                <h2 className="mt-2 font-heading text-2xl font-extrabold text-navy">Top Questions Right Now</h2>
                <div className="mt-6 space-y-3">
                  {mostAsked.map((f) => <FaqCard key={f.id} f={f} open={open === `ma-${f.id}`} onOpen={() => { setOpen(open === `ma-${f.id}` ? null : `ma-${f.id}`); api.post(`/faqs/public/view/${f.id}`).catch(() => {}); }} highlight prefix="faq-ma" />)}
                </div>
              </section>
            )}

            {/* CATEGORY SECTIONS */}
            {Object.entries(grouped).map(([category, list]) => (
              <section key={category} className="mb-12" data-testid={`faqs-section-${category.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <div className="flex items-center gap-3">
                  <span className="grid h-10 w-10 place-items-center rounded-xl bg-navy text-saffron">
                    {(CHIPS.find(([c]) => c === category) || [null, Package])[1] && (() => { const I = (CHIPS.find(([c]) => c === category) || [null, Package])[1]; return <I className="h-5 w-5" />; })()}
                  </span>
                  <div>
                    <h2 className="font-heading text-xl font-extrabold text-navy sm:text-2xl">{category} FAQs</h2>
                    <p className="text-[11px] font-semibold text-slate-400">{list.length} questions</p>
                  </div>
                </div>
                {INFOGRAPHICS[category] && <Infographic {...INFOGRAPHICS[category]} />}
                <div className="mt-5 space-y-3">
                  {list.map((f) => <FaqCard key={f.id} f={f} open={open === f.id} onOpen={() => openFaq(f)} />)}
                </div>
              </section>
            ))}
            {!Object.keys(grouped).length && (
              <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center" data-testid="faqs-empty">
                <Package className="mx-auto h-8 w-8 text-slate-300" />
                <p className="mt-3 font-heading text-base font-bold text-navy">No questions in this category yet</p>
              </div>
            )}
          </>
        )}
      </main>

      {/* STILL HAVE A QUESTION */}
      <section className="bg-white py-16" data-testid="faqs-help">
        <div className="mx-auto max-w-3xl px-4 text-center sm:px-6">
          <span className="mx-auto grid h-16 w-16 place-items-center rounded-2xl bg-saffron/10 text-saffron"><Headphones className="h-8 w-8" /></span>
          <h2 className="mt-5 font-heading text-2xl font-extrabold tracking-tight text-navy sm:text-3xl">Still Can't Find Your Answer?</h2>
          <p className="mx-auto mt-2 max-w-md text-sm text-slate-500">Our team is ready to help you with your shipment.</p>
          <div className="mt-7 flex flex-col justify-center gap-3 sm:flex-row">
            {waNum && <a href={`https://wa.me/91${waNum}?text=${encodeURIComponent("Hi IndiaFast, I have a question about shipping.")}`} target="_blank" rel="noreferrer" data-testid="faqs-help-whatsapp"
                         className="flex items-center justify-center gap-2 rounded-full bg-emerald-600 px-6 py-3 text-sm font-bold text-white hover:bg-emerald-500"><MessageCircle className="h-4 w-4" /> WhatsApp Us</a>}
            {contact?.phone && <a href={`tel:+91${waNum}`} data-testid="faqs-help-call"
                                  className="flex items-center justify-center gap-2 rounded-full border border-navy/20 px-6 py-3 text-sm font-bold text-navy hover:border-saffron hover:text-saffron"><Phone className="h-4 w-4" /> Call Us</a>}
            <Link to="/rate-calculator" data-testid="faqs-help-quote" className="rounded-full border border-navy/20 px-6 py-3 text-sm font-bold text-navy hover:border-saffron hover:text-saffron">Get a Quote</Link>
            <Link to="/pickup-request" data-testid="faqs-help-pickup" className="rounded-full bg-saffron px-6 py-3 text-sm font-bold text-white shadow-lg shadow-saffron/30 hover:bg-saffron-hover">Request a Pickup</Link>
          </div>
        </div>
      </section>

      {/* FOOTER CTA */}
      <section className="relative overflow-hidden bg-navy py-16 sm:py-20" data-testid="faqs-final-cta">
        <img src="https://images.pexels.com/photos/6682787/pexels-photo-6682787.jpeg?auto=compress&cs=tinysrgb&w=1920" alt="" loading="lazy" className="absolute inset-0 h-full w-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/85 to-navy/50" />
        <div className="tricolor-beam absolute inset-x-0 top-0 h-1" />
        <div className="relative mx-auto max-w-4xl px-4 text-center sm:px-6">
          <h2 className="font-heading text-3xl font-extrabold tracking-tight text-white sm:text-4xl">Ship With Confidence</h2>
          <p className="mx-auto mt-3 max-w-xl text-sm text-slate-300 sm:text-base">From your first question to final delivery, IndiaFast is here to help.</p>
          <div className="mt-7 flex flex-col justify-center gap-3 sm:flex-row">
            <Link to="/rate-calculator" data-testid="faqs-final-quote" className="rounded-full bg-saffron px-7 py-3 text-sm font-bold text-white shadow-lg shadow-saffron/30 hover:bg-saffron-hover">Get a Quote</Link>
            <Link to="/pickup-request" data-testid="faqs-final-pickup" className="rounded-full border border-white/25 px-7 py-3 text-sm font-bold text-white hover:bg-white/10">Request Pickup</Link>
            <Link to="/track" data-testid="faqs-final-track" className="rounded-full border border-white/25 px-7 py-3 text-sm font-bold text-white hover:bg-white/10">Track Shipment</Link>
          </div>
        </div>
      </section>

      <Footer contact={contact} />
    </div>
  );
}

function FaqCard({ f, open, onOpen, highlight, prefix = "faq" }) {
  return (
    <div className={`overflow-hidden rounded-2xl border bg-white shadow-sm transition-all ${open ? "border-saffron/40 shadow-lg" : "border-slate-200 hover:border-saffron/30"} ${highlight ? "ring-1 ring-saffron/20" : ""}`}
         data-testid={`${prefix}-card-${f.id}`}>
      <button onClick={onOpen} data-testid={`${prefix}-toggle-${f.id}`}
              className="flex w-full items-center justify-between gap-3 px-5 py-4 text-left">
        <span className="flex items-center gap-3">
          <span className={`hidden rounded-full px-2.5 py-0.5 text-[9px] font-bold uppercase tracking-wider sm:block ${highlight ? "bg-saffron/10 text-saffron" : "bg-slate-100 text-slate-500"}`}>{f.category}</span>
          <span className="text-sm font-bold text-navy">{f.question}</span>
        </span>
        <ChevronDown className={`h-4 w-4 shrink-0 transition-transform ${open ? "rotate-180 text-saffron" : "text-slate-400"}`} />
      </button>
      <div className={`grid transition-all duration-300 ${open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}>
        <div className="overflow-hidden">
          <div className="border-t border-slate-100 px-5 py-4" data-testid={`${prefix}-answer-${f.id}`}>
            <p className="text-sm leading-relaxed text-slate-600">{f.answer}</p>
            {f.related_href && (
              <Link to={f.related_href} data-testid={`${prefix}-related-${f.id}`}
                    className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-navy px-4 py-2 text-[11px] font-bold text-white transition-colors hover:bg-express">
                {f.related_label || "Learn more"} <ArrowRight className="h-3 w-3" />
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
